import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { Row } from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BorrowingBaseTestTypeGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';

type VerificationBulkUpdateWaiverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  queuedReceivableWaiverUpdates: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  setQueuedReceivableWaiverUpdates: React.Dispatch<
    React.SetStateAction<ReceivableBorrowingBaseTestWaiverInputGQLType[]>
  >;
  activeReceivableWaivers: ReceivableBorrowingBaseTestWaiverGQLType[];
  selectedRows: Row<ReceivableBorrowingBaseTestWaiverInputGQLType>[];
  resetRowSelection: () => void;
};
export const VerificationBulkUpdateWaiverModal = ({
  isOpen,
  onClose,
  queuedReceivableWaiverUpdates,
  setQueuedReceivableWaiverUpdates,
  activeReceivableWaivers,
  selectedRows,
  resetRowSelection,
}: VerificationBulkUpdateWaiverModalProps) => {
  // undefined = not selected
  // null = explicitly remove start/expiration date
  const [selectedStartDate, setSelectedStartDate] = useState<
    Util.FullDateStr | undefined | null
  >(undefined);
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<
    Util.FullDateStr | undefined | null
  >(undefined);

  const onCloseModal = () => {
    setSelectedStartDate(undefined);
    setSelectedExpirationDate(undefined);
    resetRowSelection();

    onClose();
  };

  const getLatestWaiverStartDate = useCallback(() => {
    const selectedRowsStartDates: Util.Maybe<Util.FullDateStr>[] = _.map(
      selectedRows,
      (row) => row.getValue('waiverStartReportingDate'),
    );
    const latestStoredWaiverStartDate = _.first(
      _.orderBy(selectedRowsStartDates, 'desc'),
    );

    const latestWaiverStartDate =
      selectedStartDate ?? latestStoredWaiverStartDate;

    if (latestWaiverStartDate) {
      return commonDate.fullDateStrToDate(latestWaiverStartDate);
    }
    return null;
  }, [selectedStartDate, selectedRows]);

  const getEarliestWaiverExpirationDate = useCallback(() => {
    const selectedRowsExpirationDates: Util.Maybe<Util.FullDateStr>[] = _.map(
      selectedRows,
      (row) => row.getValue('waiverExpirationReportingDate'),
    );
    const earlierstStoredWaiverExpirationDate = _.first(
      _.orderBy(selectedRowsExpirationDates, 'asc'),
    );

    const earliestWaiverExpirationDate =
      selectedExpirationDate ?? earlierstStoredWaiverExpirationDate;

    if (earliestWaiverExpirationDate) {
      return commonDate.fullDateStrToDate(earliestWaiverExpirationDate);
    }

    return null;
  }, [selectedRows, selectedExpirationDate]);

  const onSaveDateUpdate = () => {
    const newQueuedReceivableWaiverUpdates = [...queuedReceivableWaiverUpdates];

    _.forEach(selectedRows, (row) => {
      const finleyReceivableId: string = row.getValue('finleyReceivableId');
      const borrowingBaseTestType: BorrowingBaseTestTypeGQLType = row.getValue(
        'borrowingBaseTestType',
      );
      const borrowingBaseTestKey: string = row.getValue('borrowingBaseTestKey');

      const existingStoredWaiverIndex = _.findIndex(
        activeReceivableWaivers,
        (receivableWaiver) => {
          return (
            receivableWaiver.finleyReceivableId === finleyReceivableId &&
            receivableWaiver.borrowingBaseTestType === borrowingBaseTestType &&
            receivableWaiver.borrowingBaseTestKey === borrowingBaseTestKey
          );
        },
      );

      const existingStoredWaiver =
        activeReceivableWaivers[existingStoredWaiverIndex];

      const existingUpdateQueuedWaiverIndex = _.findIndex(
        queuedReceivableWaiverUpdates,
        (queuedReceivableWaiverUpdate) => {
          return (
            queuedReceivableWaiverUpdate.finleyReceivableId ===
              finleyReceivableId &&
            queuedReceivableWaiverUpdate.borrowingBaseTestType ===
              borrowingBaseTestType &&
            queuedReceivableWaiverUpdate.borrowingBaseTestKey ===
              borrowingBaseTestKey
          );
        },
      );

      const existingUpdateQueuedWaiver =
        queuedReceivableWaiverUpdates[existingUpdateQueuedWaiverIndex];

      if (existingStoredWaiver && existingUpdateQueuedWaiver) {
        // coerce dates to the same falsy format for comparison purposes
        const isExistingStoredWaiverRecord =
          (existingStoredWaiver.waiverStartReportingDate ?? null) ===
            (selectedStartDate ?? null) &&
          (existingStoredWaiver.waiverExpirationReportingDate ?? null) ===
            (selectedExpirationDate ?? null);

        // if new update is exact same as stored waiver and waiver exists in queue
        // remove from queue
        if (isExistingStoredWaiverRecord) {
          newQueuedReceivableWaiverUpdates.splice(
            existingUpdateQueuedWaiverIndex,
            1,
          );
        } else {
          // otherwise replace the existing waiver in queue
          const updatedWaiver = {
            ...existingUpdateQueuedWaiver,
            waiverStartReportingDate:
              selectedStartDate !== undefined // we want to keep newStartDate if it's null
                ? selectedStartDate
                : existingUpdateQueuedWaiver.waiverStartReportingDate,
            waiverExpirationReportingDate:
              selectedExpirationDate !== undefined // we want to keep newExpirationDate if it's null
                ? selectedExpirationDate
                : existingUpdateQueuedWaiver.waiverExpirationReportingDate,
          };
          newQueuedReceivableWaiverUpdates.splice(
            existingUpdateQueuedWaiverIndex,
            1,
            updatedWaiver,
          );
        }
      } else if (existingStoredWaiver && !existingUpdateQueuedWaiver) {
        const isExistingStoredWaiverRecord =
          (existingStoredWaiver.waiverStartReportingDate ?? null) ===
            (selectedStartDate ?? null) &&
          (existingStoredWaiver.waiverExpirationReportingDate ?? null) ===
            (selectedExpirationDate ?? null);

        // if new update is exact same as stored waiver and waiver does not exist in queue
        // do nothing

        // if new update is not the same as stored waiver and waiver does not exist in queue
        // push waiver to queue
        if (!isExistingStoredWaiverRecord) {
          const {
            id: _id,
            created_at: _created_at,
            updated_at: _updated_at,
            lastUpdatedUserId: _lastUpdatedUserId,
            lastUpdatedOrganizationName: _lastUpdatedOrganizationName,
            __typename: _typename,
            ...waiverInputs
          } = existingStoredWaiver;

          const newWaiverForUpdateQueue: ReceivableBorrowingBaseTestWaiverInputGQLType = {
            ...waiverInputs,
            waiverStartReportingDate:
              selectedStartDate !== undefined // we want to keep newStartDate if it's null
                ? selectedStartDate
                : existingStoredWaiver.waiverStartReportingDate,
            waiverExpirationReportingDate:
              selectedExpirationDate !== undefined // we want to keep newExpirationDate if it's null
                ? selectedExpirationDate
                : existingStoredWaiver.waiverExpirationReportingDate,
          };

          newQueuedReceivableWaiverUpdates.push(newWaiverForUpdateQueue);
        }
      } else if (!existingStoredWaiver && existingUpdateQueuedWaiver) {
        // if there is no stored waiver record but there is an existing queue record
        // remove existing queued waiver with new one
        const updatedWaiver = {
          ...existingUpdateQueuedWaiver,
          waiverStartReportingDate:
            selectedStartDate !== undefined // we want to keep newStartDate if it's null
              ? selectedStartDate
              : existingUpdateQueuedWaiver.waiverStartReportingDate,
          waiverExpirationReportingDate:
            selectedExpirationDate !== undefined // we want to keep newExpirationDate if it's null
              ? selectedExpirationDate
              : existingUpdateQueuedWaiver.waiverExpirationReportingDate,
        };
        newQueuedReceivableWaiverUpdates.splice(
          existingUpdateQueuedWaiverIndex,
          1,
          updatedWaiver,
        );
      }
    });
    setQueuedReceivableWaiverUpdates(newQueuedReceivableWaiverUpdates);
    setSelectedStartDate(undefined);
    setSelectedExpirationDate(undefined);
    onCloseModal();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modify Date</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <>
              <Text fontWeight={500} mt="16px" mb="8px">
                Waiver Start Date (optional)
              </Text>
              <Flex>
                <SimpleDatePicker
                  width="100%"
                  dateFormat="M/d/yy"
                  selectedDate={
                    selectedStartDate
                      ? commonDate.fullDateStrToDate(selectedStartDate)
                      : undefined
                  }
                  showTimeSelect={false}
                  setSelectedDate={(date: Date | null) =>
                    setSelectedStartDate(
                      date ? commonDate.dateToFullDateString(date) : null,
                    )
                  }
                  placeHolder="Select start date"
                  maxDate={getEarliestWaiverExpirationDate()}
                />
                <Button
                  variant="link"
                  onClick={() => setSelectedStartDate(undefined)}
                >
                  <Icon icon="xmark" type="solid" color={colors.gray[400]} />
                </Button>
              </Flex>
              <Tooltip label="Clear start date of all selected waivers">
                <Button
                  variant="link"
                  cursor="pointer"
                  onClick={() => setSelectedStartDate(null)}
                  mt="8px"
                  fontSize="14px"
                  fontWeight={500}
                  textDecoration="none"
                >
                  Clear Start Date
                </Button>
              </Tooltip>
              <Text fontWeight={500} mt="16px" mb="8px">
                Waiver Expiration Date (optional)
              </Text>
              <Flex>
                <SimpleDatePicker
                  width="100%"
                  dateFormat="M/d/yy"
                  selectedDate={
                    selectedExpirationDate
                      ? commonDate.fullDateStrToDate(selectedExpirationDate)
                      : undefined
                  }
                  showTimeSelect={false}
                  setSelectedDate={(date: Date | null) =>
                    setSelectedExpirationDate(
                      date ? commonDate.dateToFullDateString(date) : null,
                    )
                  }
                  placeHolder="Select expiration date"
                  minDate={getLatestWaiverStartDate()}
                />
                <Button
                  variant="link"
                  onClick={() => setSelectedExpirationDate(undefined)}
                >
                  <Icon icon="xmark" type="solid" color={colors.gray[400]} />
                </Button>
              </Flex>
              <Tooltip label="Clear expiration date of all selected waivers">
                <Button
                  variant="link"
                  cursor="pointer"
                  onClick={() => setSelectedExpirationDate(null)}
                  mt="8px"
                  fontSize="14px"
                  fontWeight={500}
                  textDecoration="none"
                >
                  Clear Expiration Date
                </Button>
              </Tooltip>
            </>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="aurora"
              variant="outline"
              borderWidth="2px"
              mr="32px"
              onClick={onCloseModal}
            >
              Cancel
            </Button>
            <Button colorScheme="aurora" onClick={onSaveDateUpdate}>
              Update Waiver Queue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
